import { useFlags } from 'launchdarkly-react-client-sdk';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router';
import { useQuery } from '@apollo/client/react';
import { Alert, Checkbox, Form, List, Select, Typography } from 'antd';

import useAccountQuery from 'hooks/useAccountQuery';
import useAgent from 'hooks/useAgent';
import StepContainer from 'pages/CreditAccountPage/StepContainer';
import { ACCOUNT_STATUSES, GET_AUTOPAY_STATUS } from 'queries/account';
import {
  ACCOUNT_STATUS_CODE,
  ACCOUNT_STATUS_OPERATION,
} from 'utilities/constants';
import { getCardDisplayName } from 'utilities/helpers';
import {
  APPLY_STATUS_STEPS,
  setNextStep,
  setAccountFraudStatusVariables,
  setShouldRunSubmit,
  useApplyStatusState,
} from './applyStatusState';
import useBlockingStatusMessage from './utilities/useBlockingStatusMessage';
import { useBypassStatus } from './utilities/useBypassStatus';
import {
  CLOSED_REASON,
  DEFAULT_STATUS_FORM_VALUES,
  FORM_FIELD,
  RESTRICTIVE_TIERED_WATCH_REASONS,
  STATUS_OPTIONS,
} from './utilities/applyStatusConstants';
import {
  StatusDetailsGrid,
  renderStatusFields,
} from './utilities/statusFieldUtilities';
import {
  validateFraudulentClosedReason,
  validateTWandCWReason,
} from './utilities/validators';
import { formatDate, formatDateToSubmit } from 'utilities/datesAndTimes';
import dayjs from 'dayjs';

const SelectStatusStep = ({ form }) => {
  const { accountId, customerId } = useParams();
  const agent = useAgent();
  const { account: currentViewedAccount, accounts } = useAccountQuery(
    customerId,
    accountId,
  );

  const { isFraudOps, isFdr, isFdi, isSah, isS3FraudAgent, isEscalations } =
    useFlags();

  const [statusReasons, setReasonOptions] = useState([]);

  const shouldValidateRef = useRef(false);

  const { submissionInitiated } = useApplyStatusState();

  const selectedStatusCode = Form.useWatch(FORM_FIELD.STATUS_CODE, form);
  const selectedReasonCode = Form.useWatch(FORM_FIELD.REASON_CODE, form);
  const applyToAllAccounts = Form.useWatch(
    FORM_FIELD.APPLY_TO_ALL_ACCOUNTS,
    form,
  );

  const [highlightBlockingStatusMessage, setHighlightBlockingStatusMessage] =
    useState(false);

  const statusQuery = useQuery(ACCOUNT_STATUSES, {
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'cache-and-network',
    variables: {
      customerId,
      accountIds: [accountId],
    },
  });

  const activeStatuses = useMemo(() => {
    const accountStatusData = (
      statusQuery.data?.accountFraudStatuses || []
    ).find((statusObj) => statusObj.accountId === accountId);
    return accountStatusData?.accountStatuses || [];
  }, [statusQuery.data]);
  const {
    bypassStatusesToBeReplaced,
    handleBypassStatuses,
    statusOverrideWarning,
    removingBypassStatus,
  } = useBypassStatus({
    activeStatuses,
    selectedStatusCode,
    selectedReasonCode,
    form,
  });

  const { data: autopayData } = useQuery(GET_AUTOPAY_STATUS, {
    variables: { customerId, accountId },
    skip: !customerId || !accountId,
  });

  useEffect(() => {
    if (selectedStatusCode) {
      // initialize reason options
      const statusOption = STATUS_OPTIONS.find(
        (option) => option.value === selectedStatusCode,
      );
      const reasonOptions = statusOption?.reasonCodes || [];
      setReasonOptions(reasonOptions);
      form.setFieldsValue({
        [FORM_FIELD.REASON_CODE]: reasonOptions[0].value,
      });
    }
  }, [selectedStatusCode]);

  const { isCLV9, isMultiAccount } = useMemo(() => {
    return {
      isCLV9: selectedReasonCode === CLOSED_REASON.APPLICATION_FRAUD,
      isMultiAccount:
        RESTRICTIVE_TIERED_WATCH_REASONS.includes(selectedReasonCode),
    };
  }, [selectedReasonCode]);

  const accountIds =
    isMultiAccount && applyToAllAccounts
      ? accounts.map((account) => account.id)
      : [currentViewedAccount.id];

  useEffect(() => {
    form.setFieldValue(FORM_FIELD.APPLY_TO_ALL_ACCOUNTS, isMultiAccount);
  }, [isMultiAccount]);

  const blockingStatusMessage = useBlockingStatusMessage(
    selectedStatusCode,
    selectedReasonCode,
    activeStatuses,
  );

  const onSubmit = async () => {
    shouldValidateRef.current = true;

    if (blockingStatusMessage) {
      setHighlightBlockingStatusMessage(true);
      shouldValidateRef.current = false;
      return;
    }

    const hasTW15 = bypassStatusesToBeReplaced?.find(
      (status) => status.statusCode === ACCOUNT_STATUS_CODE.TIERED_WATCH,
    );

    let fieldValues;
    try {
      fieldValues = await form.validateFields();
    } catch (error) {
      return;
    }

    shouldValidateRef.current = false;

    try {
      const { statusCode, reasonCode } = fieldValues;
      const activeStatus = activeStatuses.find(
        (status) => ACCOUNT_STATUS_CODE[status.statusCode] === statusCode,
      );

      const bypassStatusRemovalResponse = await handleBypassStatuses({
        customerId,
        accountIds,
      });

      const variables = {
        accountIds,
        customerId,
        agentName: agent?.user?.name || 'anonymous',
        operation: activeStatus
          ? ACCOUNT_STATUS_OPERATION.UPDATED
          : ACCOUNT_STATUS_OPERATION.APPLIED,
        reasonCode: `${statusCode}_${reasonCode}`,
        statusCode: statusCode,
      };
      setAccountFraudStatusVariables(variables);

      if (isCLV9) {
        setNextStep(APPLY_STATUS_STEPS.SUBMIT_CLV9_STEP);
        setShouldRunSubmit(true);
        return;
      }

      if (
        hasTW15 ||
        !bypassStatusesToBeReplaced?.length ||
        !bypassStatusRemovalResponse.errors
      ) {
        setNextStep(APPLY_STATUS_STEPS.SUBMIT_ACCOUNT_STATUS_STEP);
        setShouldRunSubmit(true);
        return;
      }
    } catch (error) {
      console.log({ error });
      return;
    }
  };

  const autopay = autopayData?.account?.autopay?.[0];
  const hasActiveAutopay =
    (autopay?.id ?? false) && autopay?.scheduleStatus === 'ACTIVE';

  return (
    <StepContainer
      title="Select Account Fraud Status"
      onContinue={onSubmit}
      buttonText="Apply Account Status"
      justifyContinueButton="right"
    >
      <Form
        form={form}
        name="applyFraudStatusForm"
        layout="vertical"
        initialValues={DEFAULT_STATUS_FORM_VALUES}
        disabled={submissionInitiated}
        validateTrigger={[]}
      >
        <StatusDetailsGrid>
          <Form.Item
            label="Status"
            name={FORM_FIELD.STATUS_CODE}
            rules={[
              {
                required: true,
                message: `Please select a category.`,
                validator: (_, value) => {
                  return new Promise((resolve, reject) => {
                    if (shouldValidateRef.current && !value) {
                      reject(new Error('Please select a category.'));
                    } else {
                      resolve();
                    }
                  });
                },
              },
            ]}
          >
            <Select
              data-testid="status-select"
              placeholder={`Select a Status`}
              options={STATUS_OPTIONS}
            />
          </Form.Item>

          <Form.Item
            label="Reason"
            name={FORM_FIELD.REASON_CODE}
            rules={[
              {
                required: true,
                message: `Please select a reason.`,
                validator: (_, value) => {
                  return new Promise((resolve, reject) => {
                    if (shouldValidateRef.current && !value) {
                      reject(new Error('Please select a reason.'));
                    } else {
                      resolve();
                    }
                  });
                },
              },
              {
                validator: (_, value) => {
                  if (!shouldValidateRef.current) {
                    return Promise.resolve();
                  }
                  return validateTWandCWReason({
                    agent,
                    form,
                    flags: { isFdi, isFdr },
                  })(_, value);
                },
              },
              {
                validator: (_, value) => {
                  if (!shouldValidateRef.current) {
                    return Promise.resolve();
                  }
                  return validateFraudulentClosedReason({
                    flags: {
                      isFraudOps,
                      isSah,
                      isS3FraudAgent,
                      isEscalations,
                    },
                  })(_, value);
                },
              },
            ]}
          >
            <Select
              data-testid="reason-select"
              placeholder={`Select a Reason`}
              options={statusReasons}
            />
          </Form.Item>

          {open &&
            renderStatusFields({
              blockingStatusMessage,
              currentViewedAccount,
              form,
              highlightBlockingStatusMessage,
            })}

          {isMultiAccount ? (
            <Form.Item
              name={FORM_FIELD.APPLY_TO_ALL_ACCOUNTS}
              valuePropName="checked"
              style={{
                gridColumn: '1/-1',
                marginBottom: 0,
              }}
            >
              <Checkbox data-testid="apply-to-all-accounts-checkbox">
                Apply status to all accounts.
              </Checkbox>
            </Form.Item>
          ) : null}

          <List
            header={
              <Typography.Text strong>Applying status to:</Typography.Text>
            }
            bordered
            size="small"
            dataSource={
              isMultiAccount && applyToAllAccounts
                ? accounts
                : [currentViewedAccount]
            }
            style={{
              gridColumn: '1/-1',
              marginBottom: 'var(--spacing)',
            }}
            renderItem={(account) => {
              return (
                <List.Item>
                  <Typography.Text>
                    {getCardDisplayName(account)}
                  </Typography.Text>
                </List.Item>
              );
            }}
          />
        </StatusDetailsGrid>

        {isCLV9 && hasActiveAutopay && (
          <Alert
            banner
            type="warning"
            title={<span>Account is enrolled in AutoPay.</span>}
            data-testid="autopay-warning-alert"
          />
        )}

        {removingBypassStatus ? (
          <Alert
            banner
            type="info"
            title={
              <span>
                <strong>{removingBypassStatus}</strong> will automatically be
                removed from the above listed accounts when the selected status
                is applied.
              </span>
            }
          />
        ) : null}

        {statusOverrideWarning && !blockingStatusMessage ? (
          <Form.Item
            name={FORM_FIELD.OVERWRITE_STATUS}
            valuePropName="checked"
            style={{ gridColumn: '1/-1' }}
            rules={[
              {
                message: 'Please acknowledge status replacement',
                validator: (_, checked) => {
                  if (!checked) {
                    return Promise.reject(
                      new Error('Please acknowledge status replacement'),
                    );
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <Checkbox data-testid="override-warning-acknowledgement">
              {statusOverrideWarning}
            </Checkbox>
          </Form.Item>
        ) : null}
      </Form>
    </StepContainer>
  );
};

export default SelectStatusStep;
